import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Button,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { SEARCH_TEXT_PLACEHOLDER } from './constants';
import { Close, PriorityHigh, Search } from '@mui/icons-material';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import SearchContainer from './SearchContainer';
import { useAxios } from '../../hooks/useAxios';
import { isOperableArray } from '../../helpers/other';
import { useEffect } from 'react';
import { MERCHANT_SEARCH } from '../../api/api';

const SearchModal = ({ open, onClose }) => {
  const isMdDown = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { state: recentSearches, setState: setRecentSearches } = useLocalStorage('searchHistory', []);
  const [searchResult, setSearchResults] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showError, setShowError] = useState(false);
  const { callAxios: getSearchResults, isLoading } = useAxios({
    callOnMount: false,
    showError: true,
    onSuccess: onSearchSuccess,
  });

  const handleClose = () => {
    setInputValue('');
    setSearchTerm('');
    setSearchResults('');
    setShowError(false);
    onClose();
  };

  useEffect(() => {
    if (searchTerm) {
      getSearchResults({
        url: MERCHANT_SEARCH,
        params: { q: searchTerm },
      });
    }
    // eslint-disable-next-line
  }, [searchTerm, recentSearches]);

  function onSearchSuccess({ data }) {
    setSearchResults(data?.data);
  }

  const handleSearch = () => {
    if (isLoading) return; // Prevent search if already loading

    try {
      const searchStr = inputValue?.trim();
      if (!searchStr || searchStr.length < 3) {
        setShowError(true);
        setTimeout(() => setShowError(false), 2000);
        return;
      }
      setShowError(false);
      // Update recent search history
      let updatedHistory = [searchStr];

      if (isOperableArray(recentSearches)) {
        // Keep the latest 10 searches, removing duplicates
        updatedHistory = [searchStr, ...recentSearches.filter((item) => item !== searchStr)].slice(0, 10);
      }
      setRecentSearches(updatedHistory);
      setSearchTerm(searchStr);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: isMdDown ? '80%' : '600px',
          height: isMdDown ? '80%' : '600px',
          borderRadius: '8px',
          backgroundColor: 'white',
          padding: '0px',
        },
      }}
      data-testid="search-modal"
      disableEscapeKeyDown={false}
    >
      <DialogContent
        sx={{ padding: '0px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      >
        <Box>
          <TextField
            data-testid="global-search-input"
            fullWidth
            placeholder={SEARCH_TEXT_PLACEHOLDER}
            variant="standard"
            autoComplete="off"
            value={inputValue}
            disabled={isLoading}
            sx={{
              '& .MuiInputBase-root': {
                padding: '12px',
                fontSize: '14px',
              },
              '& .MuiInputBase-input': {
                padding: 0,
                height: '30px',
              },
            }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <>
                    {(searchTerm || inputValue) && (
                      <IconButton
                        onClick={() => {
                          setInputValue('');
                          setSearchTerm('');
                        }}
                        disabled={isLoading}
                        sx={{ mr: 1 }}
                      >
                        <Close />
                      </IconButton>
                    )}
                    <Button
                      onClick={handleSearch}
                      disabled={isLoading}
                      sx={{
                        p: 0,
                        width: '20px',
                        height: '30px',
                        border: '1px solid grey.500',
                        opacity: isLoading ? 0.7 : 1,
                      }}
                      variant="contained"
                      color="info"
                    >
                      <Search fontSize="small" />
                    </Button>
                  </>
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (!e.target.value) {
                setSearchTerm('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                e.preventDefault();
                handleSearch();
              }
            }}
          />

          {showError && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: (theme) => theme.palette.error.main,
                textAlign: 'left',
                px: 2,
                pt: 1,
              }}
            >
              <PriorityHigh fontSize="small" />
              <Typography>Please enter at least 3 characters</Typography>
            </Box>
          )}
        </Box>

        <SearchContainer
          loading={isLoading}
          searchTerm={searchTerm}
          inputValue={inputValue}
          setInputValue={setInputValue}
          setSearchTerm={setSearchTerm}
          recentSearches={recentSearches}
          searchResult={searchResult}
          setRecentSearches={setRecentSearches}
          onClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
